import { AuthOptions, createAuthLink } from 'aws-appsync-auth-link'
import { createSubscriptionHandshakeLink } from 'aws-appsync-subscription-link'

import { ApolloLink, HttpLink, split } from '@apollo/client'
import { RetryLink } from '@apollo/client/link/retry'
import { setContext } from '@apollo/client/link/context'

import { AUTH_TYPE } from '@flowwai-frontend/shared/common'

import {
   HTTP_ACCEPT_HEADER_VALUE,
   HTTP_CACHE_CONTROL_HEADER_VALUE,
   HTTP_CONTENT_TYPE_HEADER_VALUE,
   HTTP_PRAGMA_HEADER_VALUE
} from '../../../constants/HttpHeaderValuesConstants'
import { getMainDefinition } from '@apollo/client/utilities'

interface GetAuthLinkProps {
   url: string
   subscriptionUrl: string
   mutationsWithSubscriptions: string[]
   headers?: Record<string, string>
   region: string
   getAccessToken: () => Promise<string | void>
   clearAccessTokenCookie: () => void
   customLinks?: {
      additiveLinks: Array<ApolloLink>
      directionalLinks: Array<ApolloLink>
      terminatingLinks: Array<ApolloLink>
      errorLinks: Array<ApolloLink>
   }
}

export const getAuthLink = (props: GetAuthLinkProps): ApolloLink => {
   const {
      url,
      subscriptionUrl,
      mutationsWithSubscriptions,
      region,
      getAccessToken,
      customLinks = {
         additiveLinks: [],
         directionalLinks: [],
         terminatingLinks: [],
         errorLinks: []
      },
      clearAccessTokenCookie,
      headers
   } = props
   const auth: AuthOptions = {
      type: AUTH_TYPE.AWS_LAMBDA,
      token: async () => {
         const accessToken = await getAccessToken()

         return accessToken ? `Bearer ${accessToken}` : ''
      }
   }

   const retryLink = new RetryLink({
      delay: {
         initial: 10,
         max: Infinity,
         jitter: true
      },
      attempts: {
         max: 2,
         retryIf: async (error, _operation) => {
            const isUnauthorizedError = error.statusCode === 401

            let hasAccessToken = false
            if (isUnauthorizedError) {
               clearAccessTokenCookie()

               const accessToken = await getAccessToken()

               hasAccessToken = !!accessToken

               if (hasAccessToken) {
                  _operation.setContext({
                     ..._operation.getContext(),
                     headers: {
                        ..._operation.getContext()['headers'],
                        Authorization: `Bearer ${accessToken}`
                     }
                  })
                  return true
               }
            }

            return false
         }
      }
   })

   const authLink = createAuthLink({ url, region, auth })

   const httpLink = new HttpLink({ uri: url, headers })

   const subscriptionsAndRelatedMutationsLink = new HttpLink({
      uri: subscriptionUrl,
      headers
   })

   const graphQLOperationsSplitLink = split(
      ({ query: operation }) => {
         const definition = getMainDefinition(operation)

         if (definition.kind === 'OperationDefinition') {
            if (definition.operation === 'query') return false

            if (definition.operation === 'subscription') {
               return true
            }

            const isMutationWithSubscription = mutationsWithSubscriptions.find(
               mutation => mutation === definition.name?.value
            )
            return isMutationWithSubscription ? true : false
         }
         return false
      },
      subscriptionsAndRelatedMutationsLink,
      httpLink
   )

   // Custom middleware link to add additional headers
   const customHeadersLink = setContext((_, { headers }) => ({
      headers: {
         ...headers,
         'Content-Type': HTTP_CONTENT_TYPE_HEADER_VALUE,
         Accept: HTTP_ACCEPT_HEADER_VALUE,
         Pragma: HTTP_PRAGMA_HEADER_VALUE,
         'Cache-Control': HTTP_CACHE_CONTROL_HEADER_VALUE
      }
   }))

   const link = ApolloLink.from([
      customHeadersLink,
      ...(customLinks?.additiveLinks || []),
      ...(customLinks?.errorLinks || []),
      retryLink,
      authLink,
      createSubscriptionHandshakeLink(
         { url: subscriptionUrl, region: region, auth },
         graphQLOperationsSplitLink
      )
   ])

   return link
}
